import { sumBy } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import marketContractHorizontalV2 from '@/components/market/v2/market-contract-horizontal-v2/market-contract-horizontal-v2.vue';
import purchaseDesktop from '@/components/purchase/purchase-desktop/purchase-desktop.vue';
import marketPayout from '@/components/market/market-payout/market-payout.vue';
import loaderMixin from '@/mixins/loader';
import Contract from '@/models/contract';
import { contractOwnershipNum, hasMarketPayout } from '@/utils/helpers';
import scrollTo from '@/utils/scrollTo';
import { MODALS } from '@/components/app/app-modals/app-modals';

// transition time + buffer
export const OFFSET_CHECK_TIMEOUT = 300 + 150;
export const MARKET_CONTRACTS_SHOW_MORE_NUM = 3;

export default {
  props: {
    market: {
      type: Object,
      required: true,
    },
    expandingAll: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAllContracts: false,
      isExpandingAll: this.expandingAll,
      togglePositionTop: null,
      reinitializeAttempt: 1,
    };
  },
  mixins: [
    loaderMixin,
  ],
  components: {
    marketContract: marketContractHorizontalV2,
    marketPayout,
    purchaseDesktop,
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapGetters('user', ['isLoggedIn']),
    ...mapState('trade', ['tradeOptions']),
    ...mapState('popup', ['currentPopup']),
    showContractsHeader() {
      return this.isLayoutDesktop;
    },
    contractsToShow() {
      return (!this.showAllContracts && this.shouldShowToggleMoreButton) ?
        this.filteredContracts.slice(0, MARKET_CONTRACTS_SHOW_MORE_NUM) :
        this.filteredContracts;
    },
    leftContractsToShow() {
      return this.showAllContracts ?
        [] :
        this.filteredContracts.slice(MARKET_CONTRACTS_SHOW_MORE_NUM, this.filteredContracts.length);
    },
    shouldShowToggleMoreButton() {
      return this.expandingAll && this.filteredContracts.length > MARKET_CONTRACTS_SHOW_MORE_NUM;
    },
    toggleMoreModifiers() {
      return {
        'market-list-item-expandable__contracts-toggle-more': true,
        'market-list-item-expandable__contracts-toggle-more--active': this.showAllContracts,
      };
    },
    contractLayout() {
      return this.isLayoutDesktop ? 'dashboard' : 'default';
    },
    titleMobileClassModifiers() {
      return {
        'market-list-item-expandable__mobile-title': true,
        'market-list-item-expandable__mobile-title--has-open-offers': this.showOpenOffersIndicator,
      };
    },
    toggleMobileClassModifiers() {
      return {
        'market-list-item-expandable__mobile-toggle': true,
        'market-list-item-expandable__mobile-toggle--expanded': this.expanded,
      };
    },
    titleDesktopClassModifiers() {
      return {
        'market-list-item-expandable__desktop-title': true,
        'market-list-item-expandable__desktop-title--has-open-offers': this.showOpenOffersIndicator,
      };
    },
    toggleDesktopClassModifiers() {
      return {
        'market-list-item-expandable__desktop-toggle': true,
        'market-list-item-expandable__desktop-toggle--expanded': this.expanded,
      };
    },
    marketImageStyleModifiers() {
      const baseGradient = 'linear-gradient(to right, rgba(78, 109, 115, 0.12), rgba(78, 109, 115, 0.16))';
      return {
        'background-image': `${baseGradient}, url(${this.market.marketImageUrl})`,
      };
    },
    filteredContracts() {
      return (this.market && this.market.marketContracts) ?
        this.market.marketContracts.filter(i => contractOwnershipNum(i) > 0) :
        [];
    },
    isPayoutAvailable() {
      return hasMarketPayout(this.market);
    },
    openOffers() {
      if (!this.market.isOpen) return 0;
      return sumBy(
        this.filteredContracts,
        i => i.userOpenOrdersBuyQuantity + i.userOpenOrdersSellQuantity,
      );
    },
    showOpenOffersIndicator() {
      return this.isLayoutDesktop && this.openOffers > 0;
    },
    marketKey() {
      const loggedIn = (this.isLoggedIn) ? 'logged-in' : 'logged-out';
      return this.market ? `dashboard-${this.market.marketId}-${loggedIn}` : null;
    },
  },
  methods: {
    ...mapActions('market', ['getMarket']),
    ...mapActions('popup', ['setTradePopup']),
    onToggleMore() {
      this.showAllContracts = !this.showAllContracts;
      if (!this.showAllContracts) {
        window.scrollTo(0, this.$el.offsetTop);
      }
    },
    onViewMarket(market) {
      this.$router.push({
        name: 'market-details',
        params: { marketId: market.marketId, marketUrl: market.marketUrl },
      });
    },
    onReinitializePurchase() {
      // this.reinitializeAttempt += 1;
    },
    toggleItem() {
      this.togglePositionTop = this.$el.offsetTop;
      this.$emit('toggle', {
        expanded: !this.expanded,
        market: this.market,
      });
    },
    contractKey(prefix, data) {
      const contract = new Contract(data);
      return `${prefix}-${this.marketKey}-${contract.contractId}-${contract.mode}-${this.reinitializeAttempt.toString()}`;
    },
    isPurchaseDesktopMode(contractId) {
      return this.isLayoutDesktop && this.tradeOptions &&
        this.tradeOptions.contractId === contractId;
    },
    initializePurchase() {
      const contracts = this.market.contracts || this.market.marketContracts;
      const contractIds = contracts.map(i => i.contractId);
      if (
        this.isLayoutMobile &&
        this.tradeOptions !== null &&
        this.tradeOptions.contractId !== null &&
        contractIds.includes(this.tradeOptions.contractId) &&
        this.currentPopup !== MODALS.PURCHASE
      ) {
        this.setTradePopup({
          name: MODALS.PURCHASE,
          options: {
            market: this.market,
          },
        });
      }
    },
  },
  watch: {
    expanded(value) {
      if (value && !this.expandingAll) {
        setTimeout(() => {
          scrollTo(this.$el.offsetTop, 500);
        }, OFFSET_CHECK_TIMEOUT);
      }
    },
    tradeOptions() {
      this.initializePurchase();
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/market/market-list-item-expandable/market-list-item-expandable.js